import Image from "@tiptap/extension-image";

// Tiptap's official @tiptap/extension-image is deliberately minimal — it renders a plain <img>
// with no resize handle and no delete button. Delete already works for free (selecting an image
// gives it a ProseMirror NodeSelection, and the default keymap deletes a selected node on
// Backspace/Delete) — only resize needed building. A community package
// (tiptap-extension-resize-image) exists but its own docs say "stability in VanillaJS may not be
// guaranteed" and show no non-React/Vue usage — this app mounts Tiptap via plain @tiptap/core,
// not a framework wrapper, so that risk wasn't worth taking for ~60 lines of NodeView code.
// Mirrors the MathField NodeView pattern in ./math-field.ts.
export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("width"),
        renderHTML: (attrs: { width?: string | null }) =>
          attrs.width ? { width: attrs.width } : {},
      },
      height: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("height"),
        renderHTML: (attrs: { height?: string | null }) =>
          attrs.height ? { height: attrs.height } : {},
      },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrap = document.createElement("span");
      wrap.className = "rich-image-wrap";

      const img = document.createElement("img");
      img.src = node.attrs.src;
      if (node.attrs.alt) img.alt = node.attrs.alt;
      if (node.attrs.width) img.setAttribute("width", node.attrs.width);
      if (node.attrs.height) img.setAttribute("height", node.attrs.height);

      const handle = document.createElement("span");
      handle.className = "rich-image-resize-handle";
      handle.contentEditable = "false";

      wrap.append(img, handle);

      let startX = 0;
      let startWidth = 0;

      const commitWidth = (width: number) => {
        if (typeof getPos !== "function") return;
        const pos = getPos();
        if (typeof pos !== "number") return;
        const current = editor.view.state.doc.nodeAt(pos);
        if (!current) return;
        editor.view.dispatch(
          editor.view.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, width })
        );
      };

      const onMouseMove = (e: MouseEvent) => {
        const delta = e.clientX - startX;
        const newWidth = Math.max(40, Math.round(startWidth + delta));
        img.setAttribute("width", String(newWidth));
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        commitWidth(img.getBoundingClientRect().width | 0);
      };

      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        startX = e.clientX;
        startWidth = img.getBoundingClientRect().width;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      });

      return {
        dom: wrap,
        selectNode: () => wrap.classList.add("is-selected"),
        deselectNode: () => wrap.classList.remove("is-selected"),
        update: (updatedNode) => {
          if (updatedNode.type.name !== "image") return false;
          img.src = updatedNode.attrs.src;
          if (updatedNode.attrs.alt) img.alt = updatedNode.attrs.alt;
          else img.removeAttribute("alt");
          if (updatedNode.attrs.width) img.setAttribute("width", updatedNode.attrs.width);
          else img.removeAttribute("width");
          return true;
        },
      };
    };
  },
});
